import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PlayCircle, Lightning, X } from "@phosphor-icons/react";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AssemblyLineWalkthrough — visual story of how a Lab video gets made, from
 * niche pick → published. Two parts:
 *   1. Animated 6-step assembly line (CSS + framer-motion, no real footage).
 *   2. The "watch the full demo" embed — fed by the sample_video flagged
 *      `is_walkthrough=true` from /api/sample-videos. Renders only if present.
 */
// Stable framer-motion config — module-level so we don't allocate new objects every render.
const STEP_MOTION_INITIAL = { opacity: 0, y: 20 };
const STEP_MOTION_WHILE_IN_VIEW = { opacity: 1, y: 0 };
const STEP_MOTION_VIEWPORT = { once: true, margin: "-50px" };

const STEPS = [
    {
        n: "01",
        label: "PICK A RECIPE",
        title: "Niche locked.",
        body: "Pull a vetted Recipe from the Vault — Historical Mystery, Horror, Motivation. Or AI-generate a custom one.",
    },
    {
        n: "02",
        label: "SCRIPT ENGINE",
        title: "60-second hook in 8 seconds.",
        body: "Drop the topic into Script Engine. Output: hook → conflict → evidence → twist → CTA. Calibrated for retention.",
    },
    {
        n: "03",
        label: "VOICEOVER",
        title: "ElevenLabs / OpenAI TTS.",
        body: "One signature voice across every upload. Zero face. Zero camera. Pure brand authority.",
    },
    {
        n: "04",
        label: "VISUAL STACK",
        title: "Nano Banana 2 + Sora 2.",
        body: "Cinematic shots, 9:16 vertical, photoreal. Master prompts already wired into the Recipe.",
    },
    {
        n: "05",
        label: "EDIT + ASSEMBLE",
        title: "Cuts on every beat.",
        body: "Edit timeline, on-screen text, captions, signature pacing. The Visual Authority Guide does the heavy lifting.",
    },
    {
        n: "06",
        label: "DEPLOY",
        title: "Ship it. Ranked & monetized.",
        body: "Publish to TikTok / Shorts / Reels using the Deployment Hub checklist. The algorithm rewards systems.",
    },
];

export default function AssemblyLineWalkthrough({ variant = "landing" }) {
    const [walkthrough, setWalkthrough] = useState(null);
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        api.get("/sample-videos")
            .then((r) => {
                const list = Array.isArray(r.data) ? r.data : [];
                const wt = list.find((v) => v.is_walkthrough);
                setWalkthrough(wt || null);
            })
            .catch(() => setWalkthrough(null));
    }, []);

    const url = walkthrough ? resolveUrl(walkthrough.video_url) : "";
    const isImage = url ? /\.(png|jpg|jpeg|webp|gif)(\?|$)/i.test(url) : false;
    const isDirect = !isImage && url ? /\.(mp4|webm|mov)(\?|$)/i.test(url) || url.includes("/api/sample-videos/file/") : false;

    return (
        <section
            id="six-steps"
            data-testid="assembly-line-walkthrough"
            className={`relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20 ${
                variant === "landing" ? "py-20 md:py-28" : "py-10"
            }`}
        >
            <div className="text-center mb-14">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-electric-blue/40 bg-electric-blue/10 mb-4">
                    <Lightning size={14} weight="fill" className="text-electric-blue" />
                    <span className="label-mono text-[10px] text-electric-blue">END-TO-END · ASSEMBLY LINE DEMO</span>
                </div>
                <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] mb-4">
                    From idea to published video<br />
                    <span className="text-electric-blue neon-text">in six steps.</span>
                </h2>
                <p className="text-base md:text-lg text-foreground/75 max-w-2xl mx-auto">
                    No camera. No editor. No agency. The Lab&apos;s assembly line is six handoffs &mdash;
                    each one already systemized. Here&apos;s the full path of a finished video.
                </p>
            </div>

            {/* Walkthrough hero — image or video, depending on what an admin uploaded */}
            {walkthrough && (
                <div className="max-w-5xl mx-auto mb-16" data-testid="walkthrough-video-block">
                    <div className="relative w-full aspect-video bg-black border border-electric-blue/40 neon-border overflow-hidden group">
                        {isImage ? (
                            <>
                                <img
                                    src={url}
                                    alt={walkthrough.title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />
                                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                                    <p className="label-mono text-[10px] text-electric-blue mb-1.5">INSIDE THE LAB</p>
                                    <p className="font-heading font-black text-2xl md:text-3xl tracking-tight leading-tight">
                                        {walkthrough.title}
                                    </p>
                                </div>
                            </>
                        ) : isDirect && !playing ? (
                            <>
                                <video
                                    src={url}
                                    muted
                                    playsInline
                                    preload="metadata"
                                    className="w-full h-full object-cover opacity-80"
                                />
                                <button
                                    onClick={() => setPlaying(true)}
                                    data-testid="walkthrough-play-btn"
                                    className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/20 transition-all"
                                >
                                    <div className="flex flex-col items-center gap-3">
                                        <PlayCircle size={88} weight="fill" className="text-electric-blue drop-shadow-[0_0_30px_rgba(0,123,255,0.8)]" />
                                        <span className="label-mono text-xs text-white tracking-wider">WATCH THE FULL DEMO</span>
                                    </div>
                                </button>
                            </>
                        ) : isDirect ? (
                            <video src={url} controls autoPlay playsInline className="w-full h-full object-contain bg-black" />
                        ) : (
                            <iframe
                                src={url}
                                title={walkthrough.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                className="w-full h-full"
                            />
                        )}
                    </div>
                    {!isImage && (
                        <div className="mt-3 px-1 flex items-center justify-between flex-wrap gap-2">
                            <p className="text-sm text-muted-foreground">
                                <span className="text-electric-blue font-bold">{walkthrough.title}</span>
                                {walkthrough.description ? ` — ${walkthrough.description}` : ""}
                            </p>
                            {playing && (
                                <button
                                    onClick={() => setPlaying(false)}
                                    className="inline-flex items-center gap-1.5 label-mono text-[10px] text-muted-foreground hover:text-electric-blue"
                                >
                                    <X size={12} weight="bold" /> RESET
                                </button>
                            )}
                        </div>
                    )}
                    {isImage && walkthrough.description && (
                        <p className="mt-3 text-sm text-muted-foreground text-center">
                            {walkthrough.description}
                        </p>
                    )}
                </div>
            )}

            {/* Animated assembly line — always visible, even without a video */}
            <div className="relative" data-testid="assembly-line-steps">
                {/* Connector spine — desktop only */}
                <div className="hidden lg:block absolute top-12 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric-blue/40 to-transparent" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-px bg-white/10 relative">
                    {STEPS.map((s, i) => (
                        <motion.div
                            key={s.n}
                            initial={STEP_MOTION_INITIAL}
                            whileInView={STEP_MOTION_WHILE_IN_VIEW}
                            viewport={STEP_MOTION_VIEWPORT}
                            transition={{ delay: i * 0.1, duration: 0.5 }}
                            data-testid={`assembly-step-${i}`}
                            className="bg-[#0A0A0A] hover:bg-[#0F0F10] p-5 lg:p-6 transition-all group relative"
                        >
                            <div className="relative inline-flex items-center justify-center w-10 h-10 border border-electric-blue/40 bg-black mb-4 group-hover:border-electric-blue group-hover:shadow-[0_0_20px_rgba(0,123,255,0.5)] transition-all">
                                <span className="label-mono text-electric-blue text-sm font-bold">{s.n}</span>
                            </div>
                            <p className="label-mono text-[10px] text-electric-blue mb-2">{s.label}</p>
                            <h3 className="font-heading font-bold text-base mb-2 leading-tight">{s.title}</h3>
                            <p className="text-xs text-muted-foreground leading-relaxed">{s.body}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function resolveUrl(url) {
    if (!url) return "";
    if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
    return url;
}
